// The signer every signed encryption operation goes through (spec 6.6, API
// contract section 5). `courses-devices` deliberately holds no key material: it
// takes a `signer` and hands it a nonce, and this module is what turns that
// nonce into a signature.
//
// It exists because the store speaks in the arguments a SCREEN has — a device
// row, a recovery wrapping — while the API verifies a signature over the
// arguments the CONTRACT names, which are hashes of those things. The mapping
// between the two is the table in `coursesDevices.js`, and before this module
// it lived only in that comment: every caller would have had to hash its own
// wrapping, and a caller that hashed the wrong shape would get a 403 with
// nothing in it to say why.
//
// The signing key is unwrapped ONCE per signer, not once per operation. It
// never leaves this closure — nothing returns it — so a screen can hold a
// signer for as long as it needs one without also holding the key.
import {
  keepDigest,
  keepListDigest,
  passphraseWrappingHash,
  recoveryWrappingHash,
  signingKeyWrappedHash,
  signOperation,
  unwrapSigningKey,
  wrappedMkHash,
} from '@/utils/courses-crypto';

/**
 * How a store action's `params` become the fields the API verifies. Keys are
 * the ops `signOperation` accepts; an op absent here cannot be signed, which is
 * the point — a typo in an op name fails here rather than as a 403.
 *
 * ── WHY THIS TABLE IS THE ONE THING THAT CANNOT BE GOT WRONG QUIETLY ──
 *
 * The signature covers the canonical JSON of `{ op, memberId, nonce, ...fields }`
 * and the server rebuilds that message from the request it received. A field
 * too many or too few is a message the server never computes, so the signature
 * cannot verify — and that answer is 403 `SIGNATURE_INVALID`, which is NOT
 * retryable. It would fail on every device, every time, with an error blaming
 * the key. Nothing in this repo can catch it: only the server knows.
 *
 * So the sets are read off the deployed controller, not off prose
 * (`encryption.controller.ts`, and the same table in the contract's section 5):
 *
 *   register  { deviceId, publicKey }                         controller:96
 *             { deviceId, publicKey, selfWrappedHash }        controller:89-95
 *   approve   { deviceId, wrappedMkHash }                     controller:107-110
 *   revoke    { deviceId }                                    controller:127
 *   recovery  { recoveryWrappingHash, expectedVersion }       controller:137-140
 *   passphrase        { passphraseWrappingHash, expectedVersion }   api#1125
 *   passphrase-remove { expectedVersion }                           api#1125
 *   rotate-begin  { pendingKeyId, pendingSigningKey,
 *                   pendingSigningKeyWrappedHash,
 *                   pendingRecoveryWrappingHash,
 *                   pendingPassphraseWrappingHash,
 *                   pendingPreviousWrappingHash, deviceId }      api#1134, #1147
 *   rotate-commit { pendingKeyId }                               api#1135
 *   rotate-abort-begin { pendingKeyId }                          api#1148
 *   rotate-abort  { pendingKeyId }                               api#1135
 *   reset     {}                                              controller:155
 *   cancel    {}                                              controller:166
 *
 * `wrappedMkHash` is byte-for-byte the server's `hashCanonical({ ...body })`:
 * `WrappedMkDto` whitelists exactly the four fields it hashes.
 */
const FIELDS_FOR = Object.freeze({
  // A self-approved registration carries MK wrapped to this device, and since
  // api#1092 the signature covers that wrapping's hash, exactly as an approve
  // does, so a valid signature cannot be replayed with a swapped wrapping.
  // Without a wrapping the fields stay the pair — the controller's callback is
  // a TERNARY on `body.selfWrapped`, which is why `registerThisDevice` puts the
  // wrapping into `params` as well as into the body.
  register: async ({ deviceId, publicKey, selfWrapped }) =>
    selfWrapped
      ? { deviceId, publicKey, selfWrappedHash: await wrappedMkHash(selfWrapped) }
      : { deviceId, publicKey },
  approve: async ({ deviceId, wrappedMk }) => ({
    deviceId,
    wrappedMkHash: await wrappedMkHash(wrappedMk),
  }),
  revoke: ({ deviceId }) => ({ deviceId }),
  recovery: async ({ recoveryWrapping, expectedVersion }) => ({
    recoveryWrappingHash: await recoveryWrappingHash(recoveryWrapping),
    expectedVersion,
  }),
  // #1695 / api#1125. The wrapping goes in by HASH, like the recovery one:
  // `canonicalJson` takes scalars only, so the five fields cannot go in whole.
  // `passphraseWrappingHash` destructures rather than spreads, which is what
  // keeps this byte-identical to the server's `hashCanonical({ ...dto })` even
  // when the caller is carrying something extra.
  passphrase: async ({ passphraseWrapping, expectedVersion }) => ({
    passphraseWrappingHash: await passphraseWrappingHash(passphraseWrapping),
    expectedVersion,
  }),
  // Removal covers the version and nothing else — there is no wrapping to
  // name. A signature over `{}` would be replayable onto any version, which
  // would let a removal the member cancelled be re-presented later.
  'passphrase-remove': ({ expectedVersion }) => ({ expectedVersion }),
  /**
   * Begin a rotation (#1694, api#1134 and api#1138). Signed by the CURRENT
   * bundle key.
   *
   * THE TWO HASHES ARE ALWAYS PRESENT, and null when the member has neither.
   * Null is a canonical value, so the message shape never depends on what the
   * member happens to hold — the alternative is a field set that changes with
   * the account, which is a 403 nobody can reproduce.
   *
   * Hashes rather than omission: a signature that did not cover them would let
   * a party controlling the request swap the wrapped private half for one they
   * made, and the member would commit a generation somebody else can sign for.
   */
  'rotate-begin': async ({
    pendingKeyId,
    pendingSigningKey,
    pendingSigningKeyWrapped,
    pendingRecoveryWrapping,
    pendingPassphraseWrapping,
    pendingPreviousWrapping,
    deviceId,
    keepDeviceIds,
  }) => ({
    pendingKeyId,
    pendingSigningKey,
    pendingSigningKeyWrappedHash: await signingKeyWrappedHash(pendingSigningKeyWrapped),
    pendingRecoveryWrappingHash: pendingRecoveryWrapping
      ? await recoveryWrappingHash(pendingRecoveryWrapping)
      : null,
    pendingPassphraseWrappingHash: pendingPassphraseWrapping
      ? await passphraseWrappingHash(pendingPassphraseWrapping)
      : null,
    // MK1 under the NEW code (api#1147). Hashed like the other two, and the
    // one whose omission would matter most: it wraps the CURRENT key, so an
    // unsigned field would let a party controlling the request swap in a
    // wrapping under a code they chose — while the member's own screen went on
    // telling them the code they wrote down was the way back in.
    // Same wrapping SHAPE as the recovery one, so the same hash.
    pendingPreviousWrappingHash: pendingPreviousWrapping
      ? await recoveryWrappingHash(pendingPreviousWrapping)
      : null,
    deviceId,
    // Only when sent (api#1158): a begin that keeps nobody signs the message
    // it always did.
    ...(keepDeviceIds?.length ? { keepDeviceIds: keepListDigest(keepDeviceIds) } : {}),
  }),
  // Both signed by the PENDING key, and DIFFERENT ops with identical fields
  // (decision 49). One shared `rotate` would give them byte-identical
  // canonical messages, and an abort signature could then be presented to
  // commit — which revokes every other device and cannot be undone.
  // `keep` is covered ONLY when sent (api#1156): a commit that keeps nobody
  // signs the message it always did, and one that keeps devices signs every
  // kept id and every byte of the key each is handed.
  'rotate-commit': async ({ pendingKeyId, keep }) =>
    keep?.length ? { pendingKeyId, keep: await keepDigest(keep) } : { pendingKeyId },
  // FOUR ops now, not three (api#1148). `rotate-abort-begin` carries the same
  // single field as the other two and is signed by the same key, so sharing a
  // name with `rotate-abort` would let one signature be replayed as the other —
  // and this one flips the write gate.
  'rotate-abort-begin': ({ pendingKeyId }) => ({ pendingKeyId }),
  'rotate-abort': ({ pendingKeyId }) => ({ pendingKeyId }),
  reset: () => ({}),
  cancel: () => ({}),
});

/** The ops a signer will sign, for a caller that wants to check before asking. */
export const SIGNABLE_OPS = Object.freeze(Object.keys(FIELDS_FOR));

/**
 * A member id the contract will accept. `canonicalMessage` stringifies it
 * unchanged, so `"memberId":"568"` is a different message from `"memberId":568`
 * and every signed call fails as a 403 that says nothing about why. Auth0 and
 * the profile both hand this around as a string often enough that catching it
 * here is worth more than trusting each caller.
 */
function numericMemberId(memberId) {
  const id = typeof memberId === 'string' ? Number(memberId) : memberId;
  if (typeof id !== 'number' || !Number.isFinite(id)) {
    throw new TypeError(
      `createOperationSigner: memberId must be a number, got ${JSON.stringify(memberId)}`,
    );
  }
  return id;
}

/**
 * A signer for `courses-devices`, over the member's bundle signing key.
 *
 * @param {object} args
 * @param {{ v: number, iv: string, wrapped: string }} args.signingKeyWrapped
 *   `signingKeyWrapped` from `GET /api/me/encryption`.
 * @param {Uint8Array} args.mk the master key this device holds.
 * @param {string} args.keyId the member's key id, as the wrapping was made under.
 * @param {number} args.memberId
 * @returns {Promise<(nonce: string, operation: { op: string, params?: object }) => Promise<string>>}
 *   the `signer` a `courses-devices` action takes; resolves the base64 signature.
 */
export async function createOperationSigner({ signingKeyWrapped, mk, keyId, memberId }) {
  const id = numericMemberId(memberId);
  const signingKey = await unwrapSigningKey({
    wrapping: signingKeyWrapped,
    mk,
    keyId,
    memberId: id,
  });

  return async function sign(nonce, { op, params = {} } = {}) {
    const toFields = FIELDS_FOR[op];
    if (!toFields) {
      throw new TypeError(`createOperationSigner: nothing signs "${op}"`);
    }
    const { signature } = await signOperation({
      signingKey,
      op,
      memberId: id,
      nonce,
      fields: await toFields(params),
    });
    return signature;
  };
}
